import { useMsal } from '@azure/msal-react';
import React, { useContext, useEffect, useState } from 'react';
import { getVerificationSessionCookieName } from '.';
import { AmplitudeEventType } from '../../../../analytics/amplitudeEvents';
import { ClientConfig } from '../../../../clientConfig';
import { AUTO_INSTANT_ACTIVATION_PARAMS, OPENID_PARAMS } from '../../../../types/openid_enum';
import { ClientConfigContext } from '../../../clientConfigContext';
import { useBrand } from '../../../hooks/useBrand';
import useCookie from '../../../hooks/useCookie';
import { useMyChartRedirect } from '../../../hooks/useMyChartRedirect';
import { StateQueryParam, useQueryParams } from '../../../hooks/useQueryParams';
import { useValidatedUrl } from '../../../hooks/useValidatedUrl';
import { sendEvent } from '../../../services/amplitudeClient';
import { usePIMS } from '../../../services/pims';
import getAccessToken from '../../../util/getAccessToken';
import { redirectWithSoftLogout } from '../../../util/sessionTimeout';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';
import CustomerServiceCard from '../../containers/CustomerServiceCard';
import RedirectButtonCard from '../../containers/RedirectButtonCard';
import TransitionCard from '../../containers/TransitionCard';
import { AuthenticatedRoutes } from '../../routers';
import { AnalyticsIdentityVerificationMethod, mapToAnalyticsClearErrorType } from './analytics';
import { VENDOR_NAME } from './clear';

enum PageState {
    Authorizing,
    RedirectToProve,
}

type RedirectState = {
    brand: string;
    correlation_id: string;
    ehr_system: string;
    redirect_uri: string;
};

/**
 * The Identity Verification Redirect Page.
 * After completing identity verification, the user is redirected to this page.
 */
export const RedirectPage: React.FC<ComponentLoggerProps> = ({ log }) => {
    const [pageState, setPageState] = React.useState<PageState>(PageState.Authorizing);
    const { b2cTenantConfig } = useContext<ClientConfig>(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const { getSamlSsoPage, isMyChartUrl } = useMyChartRedirect();
    const { crossRegionEnabled } = useBrand();
    const { instance, accounts } = useMsal();
    const { authorizeByIdentityVerification, isReady: isPimsApiReady } = usePIMS();

    const { queryParams, error } = useQueryParams<StateQueryParam, RedirectState>({
        requiredParams: ['state'],
    });

    // Show the new Identity Verification page if the user already has a clear session cookie or if the feature is enabled
    const azureUserId = accounts?.[0]?.localAccountId || '';
    const verificationSessionCookieName = getVerificationSessionCookieName(VENDOR_NAME, azureUserId); // In the future, this could loop over multiple vendors if needed
    const [verificationSessionId] = useCookie(verificationSessionCookieName);
    const [jwt, setJwt] = useState<string>('');

    const redirectUrl = queryParams?.redirect_uri;

    useEffect(() => {
        if (redirectUrl && pageState == PageState.Authorizing && verificationSessionId && isPimsApiReady) {
            authorize(verificationSessionId, redirectUrl);
        }
    }, [pageState, redirectUrl, verificationSessionId, isPimsApiReady]);

    /* Display the Customer Service card if the verification session, state, or redirect_uri is missing */
    if (error !== undefined || !redirectUrl) {
        log.error(`AutoInstantActivationAuthorizationPage(): Error occurred - ${error}`);
        return <CustomerServiceCard variant='closable' />;
    }

    const { brand, ehr_system: ehrSystem } = queryParams;

    const authorize = async (sessionId: string, redirectUrl: string) => {
        const jwt = await getAccessToken(instance, accounts, b2cTenantConfig.scopes, log);
        setJwt(jwt);
        try {
            const { authorizedSystem } = await authorizeByIdentityVerification({ vendor: VENDOR_NAME, sessionId });
            let myChartSamlSSOUrl: string;
            if (isMyChartUrl(redirectUrl) && crossRegionEnabled) {
                log.info('Authorization successful, redirecting to MyChart SSO page');
                // If the redirect URL is a MyChart URL and cross-region is enabled, redirect to the MyChart SSO page
                myChartSamlSSOUrl = getSamlSsoPage(authorizedSystem);
            } else {
                log.info('Authorization successful, redirecting to RedirectURL');
                myChartSamlSSOUrl = redirectUrl;
            }
            sendEvent(
                {
                    event_type: AmplitudeEventType.ClearIdentityVerification_Success,
                    event_properties: {
                        activation_method: AnalyticsIdentityVerificationMethod.CLEAR,
                    },
                },
                jwt,
            );
            redirectWithSoftLogout(instance, myChartSamlSSOUrl, validateRedirectUrl, () => {
                return sendEvent(
                    {
                        event_type: AmplitudeEventType.EHRAuthorized,
                        event_properties: {
                            activation_method: AnalyticsIdentityVerificationMethod.CLEAR,
                        },
                    },
                    jwt,
                );
            });
        } catch (e) {
            sendEvent(
                {
                    event_type: AmplitudeEventType.ClearIdentityVerification_Incomplete,
                    event_properties: {
                        activation_method: 'clear',
                        clear_incomplete_reason: mapToAnalyticsClearErrorType(e),
                    },
                },
                jwt,
            );
            setPageState(PageState.RedirectToProve);
        }
    };

    if (pageState === PageState.Authorizing) {
        return <TransitionCard title='Creating your account' />;
    } else {
        const params = {
            [OPENID_PARAMS.BRAND]: brand,
            [OPENID_PARAMS.EHR_SYSTEM]: ehrSystem,
            [OPENID_PARAMS.REDIRECT_URI]: redirectUrl,
            [AUTO_INSTANT_ACTIVATION_PARAMS.REGISTER_PHONE]: true,
        };

        return (
            <RedirectButtonCard
                title='Provide additional information'
                content='Please provide additional information to help us locate your medical record.'
                url={AuthenticatedRoutes.RootRoutes.Registration}
                onNext={() => {
                    sendEvent(
                        {
                            event_type: AmplitudeEventType.ClearIdentityVerification_Incomplete_Next,
                            event_properties: {
                                activation_method: AnalyticsIdentityVerificationMethod.CLEAR,
                            },
                        },
                        jwt,
                    );
                }}
                params={params}
            />
        );
    }
};

export default withLogger(RedirectPage, { eventSource: 'RedirectPage' });
